"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material"
import { useAuth } from "../../contexts/AuthContext"
import { getOrganization, updateOrganization } from "../../services/organization.service"

const Organization = () => {
  const { user, selectedOrganization } = useAuth()
  const organizationId = selectedOrganization?._id || user?.organizationId
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [organization, setOrganization] = useState({
    name: "",
    email: "",
    website: "",
    address: "",
    phone: "",
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  useEffect(() => {
    fetchOrganization()
  }, [organizationId])

  const fetchOrganization = async () => {
    try {
      setLoading(true)
      const response = await getOrganization(organizationId)
      if (response.data) {
        setOrganization(response.data)
      }
    } catch (error) {
      console.error("Error fetching organization:", error)
      setSnackbar({
        open: true,
        message: "Failed to load organization details",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setOrganization((prevOrg) => ({
      ...prevOrg,
      [name]: value,
    }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      const response = await updateOrganization(organizationId, organization)
      setSnackbar({
        open: true,
        message: "Organization details updated successfully",
        severity: "success",
      })
    } catch (error) {
      console.error("Error updating organization:", error)
      setSnackbar({
        open: true,
        message: "Failed to update organization details",
        severity: "error",
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prevState) => ({
      ...prevState,
      open: false,
    }))
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Organization Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your organization details and preferences
      </Typography>

      <Card>
        <CardContent>
          <form onSubmit={handleSave}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  name="name"
                  value={organization.name || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={organization.email || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={organization.website || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={organization.phone || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  multiline
                  rows={3}
                  value={organization.address || ""}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                startIcon={saving && <CircularProgress size={20} color="inherit" />}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Organization

